import {IReg} from "/@lib@/commons/registry.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {IWspEnv, IWspUiEnv, JWspUriChangeMsg} from "/@lib@/wsp/wsp.js";
import {IAction} from "/@lib@/commons/actions.js";
import {IEndPoint, IO} from "/@lib@/commons/io/io.js";
import {BoxEnumEntry, EnumActions, IBoxEnumEltWedlet, IBoxEnumProvider, WedEnumProvider} from "/@back@/edit/wed/wedlets/box/boxTags.js";
import {IItemUiEnv} from "/@back@/wsp/views/itemMain.js";
import {EWspChangesEvts, WspsLivePlace} from "/@lib@/wsp/wspsLive.js";
import {EventMgr} from "/@lib@/commons/events.js";
import {WedMgr} from "/@back@/edit/wed/wedEditor.js";
import {JSrcFields, SRC} from "/@lib@/wsp/src.js";
import {XA} from "/@lib@/commons/xml/xAddr.js";
import {WedAutoCompProviderFromEnum} from "/@back@/edit/wed/wedlets/box/boxAutoComplete.js";
import {SrcPointer} from "/@back@/wsp/widgets/srcDrawer.js";
import {WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {isWedletSingleElt} from "/@back@/edit/wed/wedlets/wedletSingleElt.js";
import {getXsltProcessor} from "/@back@/wsp/widgets/wed/enums/xsltCache.js";

/**
 * Appelé au chargement dans le WED :
 * <jslib src=":back:wsp/widgets/wed/enums/enumPrvFromXPathRefItem.js"/>
 * Déclare un svc 'wsp-wed-enumprv-xpath-refItem'
 * qui doit être déclaré dans le widget Wed. Exemple :
 * <box-input-enum enumSvc="wsp-wed-enumprv-xpath-refItem" xpathRefItem="" transform?="" xslUrl?="" msgItemUndef?=""/>
 */
export async function jslibAsyncInit(jsEndPoint: IEndPoint, elt: Element, reg: IReg<any>) {
	//TODOsc7 widget optionnel pour compat < 6.2
	reg.registerSvc('wsp-wed-enumprv-xpath-refItem', 1, (tpl: Element, wedMgr: WedMgr): IBoxEnumProvider => {
		const cache = wedMgr.getDatasForModel(tpl);
		//On mutualise la config issue du template (chargement xsl...).
		let enumPrv: EnumPrvFromRefItemTplConfig = cache['EnumPrvFromRefItemTplConfig'];
		if (!enumPrv) {
			enumPrv = new EnumPrvFromRefItemTplConfig(tpl, wedMgr);
			cache['EnumPrvFromRefItemTplConfig'] = enumPrv;
		}
		//On ne peut pas mutualiser cette facade car le xpath vers l'enum peut-être relatif à chaque point de départ et donc pointer des enums différentes.
		return new EnumPrvFromRefItem(enumPrv, wedMgr);
	});
	reg.registerSvc('wsp-wed-autoCompetePrv-xpath-refItem', 1, (tpl: Element, wedMgr: WedMgr) => {
		//On ne peut pas mutualiser cette facade car le xpath vers l'enum peut-être relatif à chaque point de départ et donc pointer des enums différentes.
		return new WedAutoCompProviderFromEnum("wsp-wed-enumprv-xpath-refItem", wedMgr, tpl);
	});
}


/**
 * Fournisseur d'enum issu d'un autre item défini par un refItem dans notre propre item.
 * <xxx xpathRefItem="" transform?="" xslUrl?=""  msgItemUndef=""/>
 * Cet objet est une facade légère qui pointe un objet de config mutualisé au niveau du template WED
 * et un cache d'enums
 */
class EnumPrvFromRefItem extends WedEnumProvider implements IBoxEnumProvider {

	onEnumChange = new EnumChangeMgr(this);

	tpl: EnumPrvFromRefItemTplConfig;

	lastCache: EnumItemCache | null = null;

	/** Cache des enums de l'atelier. */
	protected enumCaches: EnumsItemsCaches;

	constructor(tpl: EnumPrvFromRefItemTplConfig, wedMgr: WedMgr) {
		super(null);
		this.tpl = tpl;
		const reg: IReg<IEnvEnumsItemsCaches & IWspEnv> = wedMgr.reg;
		if (!reg.env.enumsItemsCaches) reg.env.enumsItemsCaches = new EnumsItemsCaches(reg);
		this.enumCaches = reg.env.enumsItemsCaches;
		wedMgr.listeners.on("killEditor", () => {
			this.lastCache?.removeProv(this);
		});
	}

	ptrItem: SrcPointer | null | undefined;

	async getAction(key: string, widget: IBoxEnumEltWedlet): Promise<IAction<IBoxEnumEltWedlet> | null> {
		await this.setEnum(widget);
		return super.getAction(key, widget);
	}

	async getActions(widget: IBoxEnumEltWedlet, filter?: string): Promise<IAction<IBoxEnumEltWedlet>[]> {
		await this.setEnum(widget);
		return super.getActions(widget, filter);
	}

	async setEnum(widget: IBoxEnumEltWedlet) {
		await this.tpl.initing;
		if (this.tpl.xslt === null) {
			// xsl configurée mais echec à sa construction.
			this._nullAction = null
			this._actions = this.tpl.errorConfigActions;
			return;
		}
		const wedMgr = widget.wedlet.wedMgr;
		let wedletFrom = widget.wedlet;
		if (typeof this.tpl.fromUpTo === 'number')
			for (let i = 0; i < this.tpl.fromUpTo; i++) wedletFrom = wedletFrom?.wedParent;
		else if (this.tpl.fromUpTo === 'firstNoVirtual')
			while (wedletFrom?.isVirtual()) wedletFrom = wedletFrom.wedParent;
		if (!wedletFrom || wedletFrom.isVirtual()) {
			this._nullAction = null;
			this._actions = this.tpl.itemUndefActions;
			this.ptrItem = undefined; //reset du contexte UI pour écoute dynamique.
			return;
		}
		const from = XA.findDomContainer(wedletFrom.wedAnchor, wedMgr.docHolder.getDocument());
		const enumRef = from ? this.tpl.xpathExp.evaluate(from, XPathResult.STRING_TYPE).stringValue : null;
		const enumCache = enumRef ? await this.enumCaches.getEnumCache(enumRef, this.tpl).getActions(this, widget) : null;
		if (enumCache) {
			this._actions = enumCache;
			this._nullAction = enumCache.nullAction;
		} else {
			this._nullAction = null;
			this._actions = this.tpl.itemUndefActions;
		}
		if (from && this.ptrItem === undefined) {
			//On essaye de trouver le ptrItem UI pour écoute des chgts.
			this.ptrItem = null;
			const nodeSrcRef = this.tpl.xpathExp.evaluate(from, XPathResult.ANY_UNORDERED_NODE_TYPE).singleNodeValue;
			if (nodeSrcRef) {
				const wedlet = WEDLET.findWedlet(wedMgr.rootWedlet, XA.from(nodeSrcRef));
				if (isWedletSingleElt(wedlet)) {
					this.ptrItem = wedlet.element instanceof SrcPointer ? wedlet.element : DOM.findNext(wedlet.element, wedlet.element, (n): n is SrcPointer => n instanceof SrcPointer)
					if (this.ptrItem) {
						if (!this._ptrItemLstn) this._ptrItemLstn = () => {this.onEnumChange.emit()}
						this.ptrItem.onSrcRefChange.add(this._ptrItemLstn);
					}
				}
			}
		}
	}

	_ptrItemLstn: () => void
}

class EnumChangeMgr extends EventMgr<() => void> {

	constructor(public prov: EnumPrvFromRefItem) {super();}

	delete(lstn: () => void): boolean {
		const res = super.delete(lstn);
		if (this.size === 0) {
			//Plus de listener on se désabonne des chgts de l'enum courant et des chgts du ptrItem de l'enum.
			this.prov.lastCache?.removeProv(this.prov);
			this.prov.ptrItem?.onSrcRefChange.delete(this.prov._ptrItemLstn);
		}
		return res;
	}
}

class EnumPrvFromRefItemTplConfig {
	xpathExp: XPathExpression;
	//Remontée pour le noeud de départ de l'xpath identifient le refItem.
	fromUpTo: number | 'firstNoVirtual';
	msgItemUndef: string;
	msgNoEntryFound: string;
	transform?: string;

	initing: Promise<this>
	/** Si null == chargement de la xsl en erreur. */
	xslt?: XSLTProcessor | null;

	protected _errorConfigActions: EnumActions;
	get errorConfigActions(): EnumActions {
		if (!this._errorConfigActions) this._errorConfigActions = new EnumActions(new BoxEnumEntry(null).setEnabled(false).setLabel("Chargement du composant en erreur"));
		return this._errorConfigActions;
	}

	protected _itemUndefActions: EnumActions;
	get itemUndefActions(): EnumActions {
		if (!this._itemUndefActions) this._itemUndefActions = new EnumActions(new BoxEnumEntry(null).setEnabled(false).setLabel(this.msgItemUndef));
		return this._itemUndefActions;
	}

	protected _noEntryActions: EnumActions;
	get noEntryActions(): EnumActions {
		if (!this._noEntryActions) this._noEntryActions = new EnumActions(new BoxEnumEntry(null).setEnabled(false).setLabel(this.msgItemUndef));
		return this._noEntryActions;
	}

	constructor(tpl: Element, wedMgr: WedMgr) {
		const reg: IReg<IWspUiEnv & IItemUiEnv> = wedMgr.reg;
		const doc = wedMgr.docHolder.getDocument();
		let xPath = tpl.getAttribute("xpathRefItem");

		if (xPath?.startsWith("ancestor::") || xPath?.startsWith("ancestor-or-self::") || xPath?.startsWith("//"))
			this.fromUpTo = "firstNoVirtual";
		else {
			this.fromUpTo = 0;
			while (xPath.startsWith("../")) {
				this.fromUpTo++;
				xPath = xPath.substring(3);
			}
		}
		this.xpathExp = doc.createExpression(xPath, (prefix) => DOM.lookupNamespaceURI(doc, prefix));
		this.msgItemUndef = tpl.getAttribute("msgItemUndef") || "L'item spécifiant les valeurs n'est pas défini";
		this.msgNoEntryFound = tpl.getAttribute("msgNoEntryFound") || "Aucune entrée trouvée";
		this.transform = tpl.getAttribute('transform');
		// Xsl
		if (tpl.hasAttribute("xslUrl")) {
			this.initing = getXsltProcessor(reg.env.itemType.datas.wspResUrl, tpl).then((xslt) => {
				this.xslt = xslt;
				return this;
			}).catch((e) => {
				console.error(e);
				this.xslt = null; //null => xsl en erreur
				return this;
			});
		} else {
			this.initing = Promise.resolve(this);
		}
	}
}

/**
 * Gestionnaire des caches des enums associés à l'atelier.
 * Caches accessibles via wsp.reg.env.enumsItemsCaches (IEnvEnumsItemsCaches).
 */
class EnumsItemsCaches extends Map<string, EnumItemCache[]> {

	protected place: WspsLivePlace;

	constructor(public reg: IReg<IWspEnv>) {
		super();
		this.place = reg.env.wsp.newPlaceWsp();
		this.place.eventsMgr.on("wspUriChange", (msg: JWspUriChangeMsg, from) => {
			if (msg.wspCd !== this.reg.env.wsp.code) return;
			for (let caches of this.values()) {
				for (let c of caches) {
					if (c.srcEnum) {
						if ((msg.type === EWspChangesEvts.r && SRC.isSubUriOrEqual(msg.srcUri, c.srcEnum.srcUri))
							|| (msg.type === EWspChangesEvts.u && SRC.srcRef(c.srcEnum) === SRC.srcRef(msg))) {
							c.resetEnum();
						}
					}
				}
			}
		});
		const resetAll = () => {
			for (let caches of this.values()) {
				for (let c of caches) c.resetEnum();
			}
		}
		this.place.eventsMgr.on("wspLiveStateChange", resetAll);
		this.place.eventsMgr.on("onConnectionRenewed", resetAll);
	}

	getEnumCache(srcRef: string, tpl: EnumPrvFromRefItemTplConfig): EnumItemCache {
		let caches = this.get(srcRef);
		if (caches == null) {
			caches = []
			this.set(srcRef, caches);
		}
		for (let c of caches) {
			if (c.tpl.transform === tpl.transform && c.tpl.xslt === tpl.xslt) return c;
		}
		const c = new EnumItemCache(srcRef, tpl, this.reg);
		caches.push(c);
		return c;
	}
}

interface IEnvEnumsItemsCaches {
	enumsItemsCaches: EnumsItemsCaches
}

/**
 * Cache de l'enum pour une src de l'atelier avec les mêmes paramètres transform et xsl.
 */
class EnumItemCache {
	srcEnum: JSrcFields;

	protected actions: EnumActions | null;

	uses = new Set<EnumPrvFromRefItem>();

	protected fetchPending: Promise<void> | null;

	constructor(public srcRef: string, public tpl: EnumPrvFromRefItemTplConfig, public reg: IReg<IWspEnv>) {}

	async getActions(prov: EnumPrvFromRefItem, uiCtx: HTMLElement): Promise<EnumActions> {
		if (prov.lastCache !== this) {
			prov.lastCache?.uses.delete(prov);
			prov.lastCache = this;
			this.uses.add(prov);
		}
		if (!this.actions) await (this.fetchPending || (this.fetchPending = this.fetchEnum(uiCtx))); //Api public on retourne 1er appel
		return this.actions;
	}

	removeProv(prov: EnumPrvFromRefItem) {
		this.uses.delete(prov);
		prov.lastCache = null;
	}

	resetEnum(): boolean {
		this.fetchPending = null;
		this.raceCond++;
		if (this.actions) {
			this.actions = null;
			for (const facade of this.uses) {
				facade.onEnumChange.emit();
			}
			return true;
		}
		return false;
	}


	protected raceCond = 0

	async fetchEnum(uiCtx: HTMLElement | null): Promise<void> {
		try {
			const raceCond = this.raceCond;
			const wsp = this.reg.env.wsp;
			if (!wsp.isAvailable) await wsp.waitForAvailable(uiCtx);
			const qs = IO.qs("format", "stream", "param", wsp.code, "refUri", this.srcRef, "fields", "srcUri*srcId*srcDt", "204", "transform", this.tpl.transform, "srcTrashed", "false");
			const resp = await wsp.wspServer.config.wspSrcUrl.fetch(qs, 'dom');
			if (raceCond !== this.raceCond) return this.fetchPending || await (this.fetchPending = this.fetchEnum(uiCtx));
			if (resp.ok) {
				this.srcEnum = JSON.parse(resp.headers.get("X-SCFIELDS"));
				let dom = resp.asDom;
				if (dom && this.tpl.xslt) dom = this.tpl.xslt.transformToDocument(dom);
				if (dom) {
					this.actions = WedEnumProvider.buildActions(dom.documentElement);
					if (this.actions.length === 0) this.actions = this.tpl.noEntryActions;
				} else {
					this.actions = this.tpl.noEntryActions;
				}
			} else {
				this.srcEnum = null;
				this.actions = this.tpl.itemUndefActions;
			}
			this.fetchPending = null;
		} catch (e) {
			this.fetchPending = null;
			this.actions = this.tpl.itemUndefActions;
		}
	}
}
